import { useValue } from "@legendapp/state/react";
import { Icon } from "@repo/ui/components/icon";
import { Tooltip, TooltipContent, TooltipTrigger } from "@repo/ui/tooltip";
import { format } from "date-fns";
import type { ReactElement } from "react";

import { datasets$ } from "~/stores/datasets";
import { members$ } from "~/stores/members";

/** Wraps children in a tooltip explaining the verified badge. */
export const VerifiedTooltip = ({
  datasetId,
  children,
}: {
  datasetId: string;
  children: ReactElement;
}) => {
  const verifiedAt = useValue(datasets$[datasetId].verifiedAt);
  const verifierName = useValue(() => {
    const id = datasets$[datasetId].verifiedBy.get();
    if (!id) return null;
    return members$.get()?.find((m) => m.userId === id)?.user.name ?? null;
  });

  const attribution = [
    verifierName ? `Verified by ${verifierName}` : null,
    verifiedAt ? format(new Date(verifiedAt), "MMM d, yyyy") : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <Tooltip>
      <TooltipTrigger render={children} />
      <TooltipContent
        side="right"
        sideOffset={32 + 12}
        className="flex flex-col items-start gap-1 p-2"
        onContextMenu={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-1">
          <Icon name="verified-fill" className="size-3.5 text-foreground" />
          <span className="font-medium text-xs">Verified</span>
        </div>
        {attribution && (
          <p className="text-muted-foreground text-xs">{attribution}</p>
        )}
      </TooltipContent>
    </Tooltip>
  );
};
